import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Leaf,
  RefreshCw,
  Settings,
  Zap,
  Users,
  MessageSquare,
  FileText,
  Code,
  Languages,
  Calculator,
  Dna,
  Briefcase,
  BarChart3,
  ClipboardList,
  ShieldCheck,
  Mail,
  Scale,
  FileSearch,
  Mic,
  Receipt,
  Workflow,
  Target,
  Handshake,
  Presentation,
  Search,
} from "lucide-react";
import { useI18n } from "@/i18n";
import { useState } from "react";

type Category = 'productivity' | 'enterprise' | 'agent' | 'education' | 'all';

const apps = [
  // Knowledge & Productivity
  {
    icon: MessageSquare,
    name: "AI Chat",
    nameCn: "AI 对话",
    description: "Conversational AI assistant",
    descriptionCn: "对话式 AI 助手",
    category: 'productivity' as Category,
  },
  {
    icon: FileText,
    name: "Writing Assistant",
    nameCn: "写作助手",
    description: "Content generation & editing",
    descriptionCn: "内容生成与编辑",
    category: 'productivity' as Category,
  },
  {
    icon: Languages,
    name: "Translator",
    nameCn: "翻译助手",
    description: "Translate between languages",
    descriptionCn: "多语言翻译",
    category: 'productivity' as Category,
  },
  {
    icon: Code,
    name: "Code Helper",
    nameCn: "代码助手",
    description: "AI-powered coding companion",
    descriptionCn: "AI 编程伙伴",
    category: 'productivity' as Category,
  },
  {
    icon: FileSearch,
    name: "Document Analyzer",
    nameCn: "文档分析",
    description: "Summarize and extract key info from documents",
    descriptionCn: "文档摘要与关键信息提取",
    category: 'productivity' as Category,
  },
  {
    icon: Mail,
    name: "Email Drafter",
    nameCn: "邮件撰写",
    description: "Draft professional emails and replies",
    descriptionCn: "起草专业邮件与回复",
    category: 'productivity' as Category,
  },
  {
    icon: ShieldCheck,
    name: "Proofreader",
    nameCn: "校对助手",
    description: "Grammar check and style improvement",
    descriptionCn: "语法检查与文风优化",
    category: 'productivity' as Category,
  },
  {
    icon: ClipboardList,
    name: "Meeting Notes",
    nameCn: "会议纪要",
    description: "Structure and organize meeting notes",
    descriptionCn: "会议记录整理与结构化",
    category: 'productivity' as Category,
  },
  // Enterprise
  {
    icon: Users,
    name: "HR Assistant",
    nameCn: "HR 助手",
    description: "Answer employee questions on leave, benefits, and policies",
    descriptionCn: "解答员工关于假期、福利和政策的问题",
    category: 'enterprise' as Category,
  },
  {
    icon: ShieldCheck,
    name: "IT Helpdesk Bot",
    nameCn: "IT 服务台",
    description: "Troubleshoot common IT issues and guide password resets",
    descriptionCn: "排查常见 IT 问题，指导密码重置",
    category: 'enterprise' as Category,
  },
  {
    icon: Receipt,
    name: "Finance Bot",
    nameCn: "财务助手",
    description: "Handle expense queries, reimbursement rules, and budget lookups",
    descriptionCn: "处理报销规则、费用查询与预算查阅",
    category: 'enterprise' as Category,
  },
  {
    icon: ClipboardList,
    name: "Onboarding Assistant",
    nameCn: "入职助手",
    description: "Guide new hires through onboarding steps and company info",
    descriptionCn: "引导新员工完成入职流程和公司介绍",
    category: 'enterprise' as Category,
  },
  {
    icon: Scale,
    name: "Compliance Advisor",
    nameCn: "合规顾问",
    description: "Check internal documents against regulatory policies",
    descriptionCn: "对照法规政策审查内部文档",
    category: 'enterprise' as Category,
  },
  {
    icon: Briefcase,
    name: "Sales Enablement Bot",
    nameCn: "销售赋能助手",
    description: "Provide product specs, pricing, and competitive info for sales teams",
    descriptionCn: "为销售团队提供产品规格、定价和竞品信息",
    category: 'enterprise' as Category,
  },
  {
    icon: Target,
    name: "Sales Simulation",
    nameCn: "销售模拟训练",
    description: "Practice pitches and objection handling with AI role-play",
    descriptionCn: "AI 角色扮演练习销售话术与异议处理",
    category: 'enterprise' as Category,
  },
  {
    icon: Handshake,
    name: "Negotiation Training",
    nameCn: "谈判训练",
    description: "Simulate negotiation scenarios and get strategic feedback",
    descriptionCn: "模拟谈判场景并获得策略反馈",
    category: 'enterprise' as Category,
  },
  // AI Agent (Coming Soon)
  {
    icon: Mic,
    name: "Meeting Intelligence Agent",
    nameCn: "会议智能 Agent",
    description: "Recording → Transcription → Minutes + Action items",
    descriptionCn: "录音 → 转写 → 纪要 + 行动项",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: FileText,
    name: "Document Generation Agent",
    nameCn: "文档生成 Agent",
    description: "Briefs → Presentations → Data visualizations",
    descriptionCn: "摘要 → 演示文稿 → 数据可视化",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: Scale,
    name: "Contract Review Agent",
    nameCn: "合同审查 Agent",
    description: "Batch contracts → Clause extraction → Risk matrix",
    descriptionCn: "批量合同 → 条款提取 → 风险矩阵",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: Receipt,
    name: "Financial Processing Agent",
    nameCn: "财务处理 Agent",
    description: "Invoices → Classification → Reconciliation → Reports",
    descriptionCn: "发票 → 分类 → 对账 → 报表",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: BarChart3,
    name: "Data Analysis Agent",
    nameCn: "数据分析 Agent",
    description: "CSV/Excel → Cleaning → Trend analysis → Insights",
    descriptionCn: "CSV/Excel → 清洗 → 趋势分析 → 洞察报告",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: Workflow,
    name: "Workflow Automation Agent",
    nameCn: "工作流自动化 Agent",
    description: "Chain multi-step tasks with custom triggers",
    descriptionCn: "自定义触发器串联多步骤任务",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: Presentation,
    name: "PPT Master Agent",
    nameCn: "PPT 大师 Agent",
    description: "Outline → Slide generation → Layout optimization",
    descriptionCn: "大纲 → 幻灯片生成 → 排版优化",
    category: 'agent' as Category,
    comingSoon: true,
  },
  {
    icon: Search,
    name: "Research Agent",
    nameCn: "调研分析 Agent",
    description: "Topics → Source gathering → Summary reports",
    descriptionCn: "主题 → 资料收集 → 摘要报告",
    category: 'agent' as Category,
    comingSoon: true,
  },
  // Education
  {
    icon: Dna,
    name: "GRASP! Biology Tutor",
    nameCn: "GRASP! 生物导师",
    description: "O-Level Biology aligned with Singapore syllabus",
    descriptionCn: "对标新加坡 O-Level 教学大纲",
    category: 'education' as Category,
  },
  {
    icon: Leaf,
    name: "GRASP! Science: Diversity",
    nameCn: "GRASP! 科学：多样性",
    description: "Primary Science - Living & non-living things",
    descriptionCn: "小学科学 - 生物与非生物分类",
    category: 'education' as Category,
  },
  {
    icon: RefreshCw,
    name: "GRASP! Science: Cycles",
    nameCn: "GRASP! 科学：循环",
    description: "Primary Science - Life cycles & matter states",
    descriptionCn: "小学科学 - 生命周期与物质状态",
    category: 'education' as Category,
  },
  {
    icon: Settings,
    name: "GRASP! Science: Systems",
    nameCn: "GRASP! 科学：系统",
    description: "Primary Science - Plant, human & electrical systems",
    descriptionCn: "小学科学 - 植物、人体与电路系统",
    category: 'education' as Category,
  },
  {
    icon: Zap,
    name: "GRASP! Science: Energy",
    nameCn: "GRASP! 科学：能量",
    description: "Primary Science - Light, heat & photosynthesis",
    descriptionCn: "小学科学 - 光、热与光合作用",
    category: 'education' as Category,
  },
  {
    icon: Users,
    name: "GRASP! Science: Interactions",
    nameCn: "GRASP! 科学：相互作用",
    description: "Primary Science - Ecosystems, forces & adaptations",
    descriptionCn: "小学科学 - 生态系统、力与适应性",
    category: 'education' as Category,
  },
  {
    icon: Calculator,
    name: "Math Solver",
    nameCn: "数学求解器",
    description: "Solve complex math problems",
    descriptionCn: "解决复杂数学问题",
    category: 'education' as Category,
  },
  {
    icon: BookOpen,
    name: "Study Buddy",
    nameCn: "学习伙伴",
    description: "Learn and study with AI",
    descriptionCn: "AI 辅助学习",
    category: 'education' as Category,
  },
];

const AppShowcase = () => {
  const { language } = useI18n();
  const [activeCategory, setActiveCategory] = useState<Category>('productivity');

  const categories: { key: Category; labelEn: string; labelCn: string }[] = [
    { key: 'productivity', labelEn: 'Knowledge & Productivity', labelCn: '知识与效率' },
    { key: 'enterprise', labelEn: 'Enterprise', labelCn: '企业应用' },
    { key: 'agent', labelEn: 'AI Agent', labelCn: 'AI Agent' },
    { key: 'education', labelEn: 'Education', labelCn: '教育' },
  ];

  const filteredApps = apps.filter(app => app.category === activeCategory);

  return (
    <section id="apps" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {language === 'en' ? 'Explore the App Store' : '探索应用商店'}
            </h2>
            <p className="text-lg text-gray-500">
              {language === 'en'
                ? 'AI assistants for every need — all running locally on your Mac.'
                : '满足各种需求的 AI 助手 —— 全部在你的 Mac 上本地运行。'}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat.key
                    ? 'bg-[#1E88C8] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {language === 'en' ? cat.labelEn : cat.labelCn}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filteredApps.map((app, index) => (
              <Card
                key={index}
                className={`hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer border-gray-100 relative ${
                  (app as any).comingSoon ? 'opacity-90' : ''
                }`}
              >
                {(app as any).comingSoon && (
                  <Badge className="absolute top-3 right-3 bg-[#F0C040]/15 text-[#B8860B] border border-[#F0C040]/40 text-xs">
                    {language === 'en' ? 'Coming Soon' : '即将推出'}
                  </Badge>
                )}
                <CardHeader>
                  <div className="mb-3 inline-flex p-3 rounded-xl bg-[#1E88C8]/10">
                    <app.icon className="w-5 h-5 text-[#1E88C8]" />
                  </div>
                  <CardTitle className="text-lg">
                    {language === 'en' ? app.name : app.nameCn}
                  </CardTitle>
                  <CardDescription>
                    {language === 'en' ? app.description : app.descriptionCn}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-gray-400">
              {language === 'en' ? 'New apps added regularly.' : '持续更新中。'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
